import React, { useMemo } from "react";
import { format, isValid, parse, parseISO } from "date-fns";

function parseDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  const iso = parseISO(raw);
  if (isValid(iso)) return iso;

  const formats = ["dd.MM.yyyy HH:mm:ss", "dd.MM.yyyy HH:mm", "yyyy-MM-dd'T'HH:mm:ss", "yyyy-MM-dd HH:mm:ss"];
  for (const dateFormat of formats) {
    const parsed = parse(raw, dateFormat, new Date());
    if (isValid(parsed)) return parsed;
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatDateTime(value) {
  const date = parseDateValue(value);
  if (!date) return value || "";
  return format(date, "dd.MM.yyyy HH:mm");
}

function calcDelta(current, previous) {
  if (current == null || previous == null) return null;
  const curr = typeof current === "string" && current.endsWith("%") ? parseFloat(current) : Number(current);
  const prev = typeof previous === "string" && previous.endsWith("%") ? parseFloat(previous) : Number(previous);
  if (!Number.isFinite(curr) || !Number.isFinite(prev) || prev === 0) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

const SkeletonCard = () => (
  <div className="card p-5">
    <div className="h-4 w-24 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
    <div className="mt-5 h-8 w-20 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
    <div className="mt-4 h-4 w-40 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
  </div>
);

const KPICards = ({ kpi, loading }) => {
  const cards = useMemo(() => {
    const source = kpi || {};
    return [
      {
        label: "Всего лидов",
        value: source.total_leads ?? 0,
        delta: calcDelta(source.total_leads, source.total_leads_prev),
        hint: "Количество записей с учетом фильтров",
      },
      {
        label: "Конверсия",
        value: source.conversion ?? "0%",
        delta: calcDelta(source.conversion, source.conversion_prev),
        hint: "Доля лидов с качеством выше низкого",
      },
      {
        label: "Высокий потенциал",
        value: source.high_potential_pct ?? "0%",
        delta: calcDelta(source.high_potential_pct, source.high_potential_pct_prev),
        hint: "Процент лидов с качеством «высокий»",
      },
      {
        label: "Качественные лиды",
        value: source.quality_leads ?? 0,
        delta: calcDelta(source.quality_leads, source.quality_leads_prev),
        hint: "Всего лидов с качеством высокий/хороший",
      },
      {
        label: "Последний лид",
        value: source.last_lead_name || "-",
        extra: source.last_lead_datetime ? formatDateTime(source.last_lead_datetime) : "",
        meta: source.last_lead_id ? `ID: ${source.last_lead_id}` : "",
        hint: "ID, имя и время последнего лида",
      },
    ];
  }, [kpi]);

  if (loading) {
    return (
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <SkeletonCard key={`kpi-skeleton-${index}`} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
      {cards.map((card) => {
        const hasDelta = card.delta !== null && card.delta !== undefined;
        const deltaClass = card.delta > 0
          ? "text-emerald-600 dark:text-emerald-400"
          : card.delta < 0
          ? "text-rose-600 dark:text-rose-400"
          : "text-slate-400 dark:text-slate-500";

        return (
          <section key={card.label} className="card p-5">
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{card.label}</p>
            <div className="mt-4 flex items-baseline justify-between gap-3">
              <p className="truncate text-3xl font-bold tabular-nums text-slate-950 dark:text-white">{card.value}</p>
              {card.extra ? (
                <span className="shrink-0 text-xs tabular-nums text-slate-500 dark:text-slate-400">{card.extra}</span>
              ) : null}
            </div>
            <p className="mt-3 text-sm leading-5 text-slate-500 dark:text-slate-400">{card.hint}</p>
            {card.meta ? (
              <p className="mt-2 truncate text-xs font-medium tabular-nums text-slate-600 dark:text-slate-300" title={card.meta}>
                {card.meta}
              </p>
            ) : null}
            {hasDelta && card.label !== "Последний лид" ? (
              <p className={`mt-2 text-xs font-medium ${deltaClass}`} title="Динамика к предыдущему аналогичному периоду">
                {card.delta > 0 && "↑ "}
                {card.delta < 0 && "↓ "}
                {card.delta === 0 ? "0%" : `${Math.abs(card.delta)}%`} к прошлому периоду
              </p>
            ) : null}
          </section>
        );
      })}
    </div>
  );
};

export default KPICards;
